/**
 * DesktopSidebar - Shared left navigation sidebar for desktop layout
 */

import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Pencil, Users, Vote, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useClickOutside } from '../../hooks/useClickOutside';

interface DesktopSidebarProps {
  onNavigate: (path: string) => void;
  /** 提供時會多顯示「旅程管理」情境導覽區塊（成員/投票/邀請連結） */
  tripId?: string;
}

const TRIP_NAV_ITEMS = [
  { key: 'edit', label: '編輯旅程', icon: Pencil, suffix: '/edit' },
  { key: 'members', label: '成員', icon: Users, suffix: '/members' },
  { key: 'polls', label: '投票', icon: Vote, suffix: '/polls' },
  { key: 'invite', label: '邀請連結', icon: LinkIcon, suffix: '/invite' },
] as const;

export default function DesktopSidebar({ onNavigate, tripId }: DesktopSidebarProps) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const [showLogout, setShowLogout] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  useClickOutside(profileRef, () => setShowLogout(false), showLogout);

  return (
    <aside className="hidden lg:flex flex-col w-60 shrink-0 h-screen sticky top-0 bg-white border-r border-slate-100">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-6 py-7">
        <div className="w-8 h-8 rounded-lg bg-[#0dccf2] flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z" />
          </svg>
        </div>
        <span className="text-slate-900 font-bold text-base tracking-tight">Tripway</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1">
        <button
          onClick={() => onNavigate('/trips')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-sm font-medium"
        >
          <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
          </svg>
          <span>首頁</span>
        </button>
        <button
          onClick={() => onNavigate('/trips')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-sm font-medium"
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>行事曆</span>
        </button>
        <button
          onClick={() => onNavigate('/trips')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-sm font-medium"
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>個人資料</span>
        </button>

        {tripId && (
          <div className="pt-3 mt-1 border-t border-slate-100">
            <p className="px-3 pb-2 text-[11px] font-semibold text-slate-400 tracking-wide">旅程管理</p>
            <div className="space-y-1">
              {TRIP_NAV_ITEMS.map(({ key, label, icon: Icon, suffix }) => {
                const path = `/trips/${tripId}${suffix}`;
                const active = pathname.startsWith(path);
                return (
                  <button
                    key={key}
                    onClick={() => onNavigate(path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm ${
                      active
                        ? 'bg-[#f0f9fe] text-[#0dccf2] font-semibold'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium'
                    }`}
                  >
                    <Icon size={18} className="shrink-0" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* User profile */}
      <div ref={profileRef} className="relative border-t border-slate-100">
        <button
          onClick={() => setShowLogout(v => !v)}
          className="w-full flex items-center gap-3 px-6 py-5 hover:bg-slate-50 transition-colors text-left"
        >
          {user?.avatar ? (
            <img src={user.avatar} alt={user.nickname} className="w-8 h-8 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#0dccf2]/20 flex items-center justify-center shrink-0">
              <span className="text-[#0dccf2] text-xs font-bold">
                {user?.nickname?.charAt(0)?.toUpperCase() ?? 'U'}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.nickname ?? 'User'}</p>
            <p className="text-xs text-slate-400 truncate capitalize">{user?.loginSource ?? ''}</p>
          </div>
        </button>
        {showLogout && (
          <div className="absolute bottom-full left-0 right-0 mb-1 mx-3 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => { setShowLogout(false); signOut(); }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              登出
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
